import { useRef } from "react";
import "@fontsource/jetbrains-mono/400.css";

const flowSteps = [
  {
    timestamp: "09:41:23",
    title: "Ingest",
    description: "You paste, import, or type.\nCSV, text, JSON—doesn't matter.",
    visual: "data-blob",
  },
  {
    timestamp: "09:41:25",
    title: "Understand",
    description: "AI detects structure.\nFields, types, relationships.\nNo configuration.",
    visual: "schema",
  },
  {
    timestamp: "09:41:27",
    title: "Act",
    description: "Automation suggestions appear.\nYou approve or let it run.",
    visual: "automation",
  },
  {
    timestamp: "Continuous",
    title: "Evolve",
    description: "The system learns what works.\nGets smarter every day.",
    visual: "pattern",
  },
];

const DataBlobVisual = () => (
  <div className="w-full h-32 flex items-center justify-center">
    <div className="relative">
      <div className="absolute -inset-4 bg-accent/20 rounded-full blur-xl animate-pulse" />
      <div className="relative grid grid-cols-4 gap-1">
        {[...Array(16)].map((_, i) => (
          <div
            key={i}
            className="w-4 h-4 rounded-sm bg-muted-foreground/20"
            style={{ 
              opacity: 0.3 + Math.random() * 0.7,
              animationDelay: `${i * 50}ms`
            }}
          />
        ))}
      </div>
    </div>
  </div>
);

const SchemaVisual = () => (
  <div className="w-full h-32 flex items-center justify-center">
    <div className="space-y-2 text-xs font-mono text-muted-foreground">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-terminal-text" />
        <span>name: <span className="text-foreground">string</span></span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-accent" />
        <span>amount: <span className="text-foreground">currency</span></span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-primary" />
        <span>due_date: <span className="text-foreground">date</span></span>
      </div>
    </div>
  </div>
);

const AutomationVisual = () => (
  <div className="w-full h-32 flex items-center justify-center">
    <div className="space-y-1.5 text-xs font-mono">
      <div className="text-terminal-text">→ when: due_date &lt; today</div>
      <div className="text-muted-foreground pl-4">→ send: email_reminder</div>
      <div className="text-muted-foreground pl-4">→ tag: "overdue"</div>
    </div>
  </div>
);

const PatternVisual = () => (
  <div className="w-full h-32 flex items-center justify-end pr-8">
    <div className="flex items-end gap-1 h-20">
      {[40, 55, 45, 70, 65, 80, 75, 90].map((h, i) => (
        <div
          key={i}
          className="w-3 bg-primary/60 rounded-t transition-all duration-300"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  </div>
);

const getVisual = (type: string) => {
  switch (type) {
    case "data-blob": return <DataBlobVisual />;
    case "schema": return <SchemaVisual />;
    case "automation": return <AutomationVisual />;
    case "pattern": return <PatternVisual />;
    default: return null;
  }
};

export const FlowSection = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <section className="py-16 sm:py-20 md:py-28 border-t border-border">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-8 sm:mb-12">
          <span className="text-xs font-mono text-muted-foreground">[PROCESS]</span>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">How Strata thinks</h2>
        </div>
        
        {/* Horizontal scroll container */}
        <div 
          ref={scrollRef}
          className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 sm:pb-6 hide-scrollbar snap-x snap-mandatory mobile-scroll -mx-4 px-4 sm:mx-0 sm:px-0"
        >
          {flowSteps.map((step, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 w-[280px] sm:w-[300px] md:w-[340px] snap-start"
            >
              <div className="h-full p-5 sm:p-6 rounded-xl border border-border bg-card hover-lift group">
                {/* Timestamp */}
                <div className="text-xs font-mono text-accent mb-3 sm:mb-4">
                  [{step.timestamp}]
                </div>
                
                {/* Title */}
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{step.title}</h3>
                
                {/* Description */}
                <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed mb-4 sm:mb-6">
                  {step.description}
                </p>
                
                {/* Visual */}
                <div className="border-t border-border pt-4">
                  {getVisual(step.visual)}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Scroll hint for mobile */}
        <div className="flex justify-center mt-4 lg:hidden">
          <span className="text-xs text-muted-foreground">← Swipe to explore →</span>
        </div>
      </div>
    </section>
  );
};
